import {
  FulfillmentTypeListResponseType,
  SupplierGetResponseType,
  SupplierRelationshipFulfillmentType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  CountryCodeDictionary,
  isIneligibleForFulfillmentType,
  isCourierIntegrationFulfillmentType,
} from "@sellar-io/common";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { Icon } from "../../../../../../components";
import { Badge, BadgePurple } from "../../../../../../components/badge/Badge";
import { Card } from "@sellar-io/components";
import { DeliveryOrderItemType } from "../../../../../../components/fulfillmentTypeCards/CourierDeliveryOptionsCard";
import { SelectedFulfillmentTypeCard } from "../../../../../../components/fulfillmentTypeCards/SelectedFulfillmentTypeCard";
import {
  ContextOrderBasketType,
  useBasketContext,
} from "../../../../../../hooks";
import { useFulfillmentTypeEligibility } from "../../../../../../hooks/useFulfillmentTypeEligibility";
import { FulfillmentTypeRadioSelector } from "../../../../Components/FulfillmentTypeRadioSelector";
import { CheckoutSelectedFulfillmentType } from "../../../../StockList";
import { StorefrontDeliveryOptionsDialog } from "../../StorefrontDialogs/StorefrontDeliveryOptionsDialog";
import { useFulfillmentTypeList } from "../../../../../../hooks/api/v2/fulfillmentType/useFulfillmentTypeList";
import { useRemoteFeatureFlags } from "../../../../../../hooks/useRemoteFeatureFlags";
import { useIneligibleForCourierStore } from "../../../../../../hooks/useIneligibleForCourierStore";
import { DeliveryMethodRadioSelector } from "../../../../Components/DeliveryMethodRadioSelector";
import { useStorefrontBuyerPostcode } from "../../lib/useStorefrontBuyerPostcode";
import { SelectedFulfillmentTypeCardLegacy } from "~/components/fulfillmentTypeCards/SelectedFulfillmentTypeCardLegacy";
import { useSearchParams } from "react-router";
import { useSellarUser } from "../../../../../../hooks";
import { DistributionDeliveryOptionsCard } from "../../../../../../components/fulfillmentTypeCards/DistributionDeliveryOptionsCard";

export const StorefrontDeliveryOptionsCard = ({
  connection,
  supplier,
  order,
  className,
  fulfillmentType,
  onChange,
  checkoutSelectedFulfillmentType,
  supplierStorefront,
  getPreviousDefaultFulfillmentType,
  setPreviousDefaultFulfillmentType,
}: {
  connection:
    | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
    | undefined
    | null;
  fulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  supplier: SupplierGetResponseType;
  order: ContextOrderBasketType | null | undefined;
  className?: string;
  onChange?: (fulfillmentType: CheckoutSelectedFulfillmentType) => void;
  checkoutSelectedFulfillmentType?: CheckoutSelectedFulfillmentType;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  getPreviousDefaultFulfillmentType: () =>
    | CheckoutSelectedFulfillmentType
    | undefined;
  setPreviousDefaultFulfillmentType: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
}) => {
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const [searchParams] = useSearchParams();
  const { user } = useSellarUser();
  const orderFlowToken = searchParams.get("orderFlowToken");
  const isOrderFlowTokenMode = !!orderFlowToken && !user;
  const [
    storefrontDeliveryOptionsDialogActive,
    setStorefrontDeliveryOptionsDialogActive,
  ] = useState<boolean>(false);

  const directDeliveryArea = supplierStorefront.directDeliveryArea;
  const sdDeliveryArea = supplierStorefront.sdDeliveryArea;

  const { isEligibleForDirect, isEligibleForDistribution } =
    useFulfillmentTypeEligibility({
      allowCollectionOnCheckout: supplier.allowCollectionOnCheckout,
      connection,
      directDeliveryArea,
      sdDeliveryArea,
    });

  const unbundlingEnabled =
    (supplierStorefront.deliveryMethods?.length ?? 0) > 0;

  const defaultFulfillmentType = (() => {
    if (isEligibleForDistribution && isOrderFlowTokenMode) {
      return "sellarDistribution";
    }

    if (connection?.fulfillmentType) {
      return connection.fulfillmentType;
    }

    if (isEligibleForDirect) {
      return "direct";
    }

    if (isEligibleForDistribution) {
      return "sellarDistribution";
    }

    return "courier";
  })();

  useEffect(() => {
    const previousDefault = getPreviousDefaultFulfillmentType();
    setPreviousDefaultFulfillmentType(defaultFulfillmentType);

    if (
      !checkoutSelectedFulfillmentType ||
      (previousDefault !== undefined &&
        previousDefault !== defaultFulfillmentType &&
        checkoutSelectedFulfillmentType === previousDefault)
    ) {
      onChange?.(defaultFulfillmentType);
    }
  }, [
    checkoutSelectedFulfillmentType,
    defaultFulfillmentType,
    getPreviousDefaultFulfillmentType,
    onChange,
    setPreviousDefaultFulfillmentType,
  ]);

  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const changeFulfillmentType = (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => {
    if (isCourierIntegrationFulfillmentType(fulfillmentType)) return;
    onChange?.(fulfillmentType);
  };

  useForceSwitchToHaulier({
    currentFulfillmentType: checkoutSelectedFulfillmentType,
    supplier,
    connection,
    directDeliveryArea,
    sdDeliveryArea,
    unbundlingEnabled,
    onUpdate: (value) => {
      changeFulfillmentType(value);
    },
  });

  const hasDeliveryMethods = !!supplierStorefront.deliveryMethods;

  if (isOrderFlowTokenMode) {
    return (
      <Card
        className={twMerge("bg-white", "flex flex-col gap-2 p-4", className)}
      >
        <Badge icon={"TRUCK"}>Delivery options</Badge>
        <DistributionDeliveryOptionsCard
          deliveryArea={supplierStorefront.sdDeliveryArea}
          supplier={supplier}
          directFulfillmentType={undefined}
          orderItems={order?.OrderItems}
          buyerPostcode={buyerPostcode}
        />
      </Card>
    );
  }

  return (
    <>
      {supplier.onlyAllowCardPaymentWithDeposit ? (
        <SellarImportsDeliveryCard
          hasSdDeliveryArea={!!sdDeliveryArea}
          changeFulfillmentType={changeFulfillmentType}
          supplier={supplier}
          fulfillmentType={checkoutSelectedFulfillmentType ?? null}
          onClickMoreInfo={() => setStorefrontDeliveryOptionsDialogActive(true)}
        />
      ) : (
        <Card
          className={twMerge("bg-white", "flex flex-col gap-2 p-4", className)}
        >
          <div className="flex items-center justify-between gap-2">
            <Badge icon={"TRUCK"}>Delivery options</Badge>
            {isEligibleForDistribution && (
              <BadgePurple icon={"STAR"} className="bg-[#A34FD2] text-white">
                New
              </BadgePurple>
            )}
          </div>
          {!isDesktop ? (
            <StorefrontDeliveryOptionCardMobile
              supplier={supplier}
              fulfillmentType={fulfillmentType}
              connection={connection}
              setActive={setStorefrontDeliveryOptionsDialogActive}
              supplierStorefront={supplierStorefront}
              orderItems={order?.OrderItems}
              selectedFulfillmentType={
                checkoutSelectedFulfillmentType ?? defaultFulfillmentType
              }
              changeFulfillmentType={(fulfillmentType) =>
                changeFulfillmentType(fulfillmentType)
              }
            />
          ) : (
            <>
              {hasDeliveryMethods ? (
                <DeliveryMethodRadioSelector
                  connection={connection}
                  onUpdate={(value) => changeFulfillmentType(value)}
                  supplier={supplier}
                  value={checkoutSelectedFulfillmentType}
                  order={order}
                  supplierStorefront={supplierStorefront}
                  buyerPostcode={buyerPostcode}
                />
              ) : (
                <FulfillmentTypeRadioSelector
                  connection={connection}
                  onUpdate={(value) => changeFulfillmentType(value)}
                  supplier={supplier}
                  value={checkoutSelectedFulfillmentType}
                  order={order}
                  supplierStorefront={supplierStorefront}
                  buyerPostcode={buyerPostcode}
                />
              )}
              {(checkoutSelectedFulfillmentType || defaultFulfillmentType) && (
                <>
                  <div
                    onClick={() =>
                      setStorefrontDeliveryOptionsDialogActive(true)
                    }
                    className={
                      "m-0 flex cursor-pointer flex-row items-center gap-2 p-0 text-blue-800"
                    }
                  >
                    <Icon icon={"OPEN_IN_NEW"} />
                    <p className={"font-semibold"}>MORE INFO</p>
                  </div>
                </>
              )}
            </>
          )}
        </Card>
      )}

      <StorefrontDeliveryOptionsDialog
        active={storefrontDeliveryOptionsDialogActive}
        onDismiss={(fulfillmentType) => {
          if (fulfillmentType) {
            changeFulfillmentType(fulfillmentType);
          }
          setStorefrontDeliveryOptionsDialogActive(false);
        }}
        supplier={supplier}
        connection={connection}
        unAuthorisedUserPostcode={null}
        fulfillmentType={
          checkoutSelectedFulfillmentType ?? defaultFulfillmentType
        }
        order={order}
      />
    </>
  );
};

const StorefrontDeliveryOptionCardMobile = ({
  supplier,
  fulfillmentType,
  connection,
  selectedFulfillmentType,
  supplierStorefront,
  setActive,
  orderItems,
  changeFulfillmentType,
}: {
  fulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  supplier: SupplierGetResponseType;
  connection:
    | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
    | undefined
    | null;
  selectedFulfillmentType: CheckoutSelectedFulfillmentType;
  setActive: (active: boolean) => void;
  supplierStorefront: SupplierStorefrontV2GetResponseType;
  orderItems?: DeliveryOrderItemType;
  changeFulfillmentType?: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
}) => {
  const unbundlingEnabled =
    supplierStorefront.deliveryMethods &&
    supplierStorefront.deliveryMethods.length > 0;

  return unbundlingEnabled ? (
    <SelectedFulfillmentTypeCard
      supplier={supplier}
      selectedFulfillmentType={selectedFulfillmentType}
      supplierStorefront={supplierStorefront}
      onClick={() => setActive(true)}
      orderItems={orderItems}
      changeFulfillmentType={changeFulfillmentType}
    />
  ) : (
    <SelectedFulfillmentTypeCardLegacy
      supplier={supplier}
      selectedFulfillmentType={selectedFulfillmentType}
      supplierStorefront={supplierStorefront}
      onClick={() => setActive(true)}
      orderItems={orderItems}
      changeFulfillmentType={changeFulfillmentType}
      fulfillmentType={fulfillmentType}
      connection={connection}
    />
  );
};

const useForceSwitchToHaulier = ({
  currentFulfillmentType,
  supplier,
  connection,
  directDeliveryArea,
  sdDeliveryArea,
  unbundlingEnabled,
  onUpdate,
}: {
  currentFulfillmentType: CheckoutSelectedFulfillmentType;
  supplier: SupplierGetResponseType;
  connection:
    | { fulfillmentType: SupplierRelationshipFulfillmentType | null }
    | undefined
    | null;
  directDeliveryArea:
    | SupplierStorefrontV2GetResponseType["DeliveryArea"]
    | undefined;
  sdDeliveryArea:
    | SupplierStorefrontV2GetResponseType["DeliveryArea"]
    | undefined;
  unbundlingEnabled: boolean;
  onUpdate: (value: "haulier") => void;
}) => {
  const [{ order: basket }] = useBasketContext();
  const { hasItemIneligibleForCourier, setHasItemIneligibleForCourier } =
    useIneligibleForCourierStore();

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  useEffect(() => {
    if (unbundlingEnabled && hasItemIneligibleForCourier) {
      setHasItemIneligibleForCourier(false);
    }
  }, [
    unbundlingEnabled,
    hasItemIneligibleForCourier,
    setHasItemIneligibleForCourier,
  ]);

  const {
    isEligibleForHaulier,
    isEligibleForDirect,
    isEligibleForCourier,
    isEligibleForDistribution,
  } = useFulfillmentTypeEligibility({
    allowCollectionOnCheckout: supplier.allowCollectionOnCheckout,
    connection,
    directDeliveryArea,
    sdDeliveryArea,
    eligibleForHaulierOverride:
      hasItemIneligibleForCourier && palletOverrideEnabled,
  });

  const unConnectedValue = isEligibleForDirect ? "direct" : "courier";
  const initialValue = isEligibleForDistribution
    ? "sellarDistribution"
    : (connection?.fulfillmentType ?? unConnectedValue);

  const fulfillmentTypeHaulierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "haulier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: isEligibleForHaulier || palletOverrideEnabled,
    },
  );

  const fulfillmentTypeCourierQuery = useFulfillmentTypeList(
    {
      query: [
        {
          type: "SupplierId",
          value: supplier.id,
          modifier: "EQUALS",
        },
        {
          type: "FulfillmentType",
          value: "courier",
          modifier: "EQUALS",
        },
      ],
    },
    {
      enabled: isEligibleForCourier,
    },
  );

  const haulierFulfillmentType =
    fulfillmentTypeHaulierQuery.data?.pages[0]?.[0];

  const courierFulfillmentType =
    fulfillmentTypeCourierQuery.data?.pages[0]?.[0];

  useEffect(() => {
    if (unbundlingEnabled) {
      return;
    }

    const forceHaulier =
      (currentFulfillmentType === "courier" ||
        (currentFulfillmentType === undefined && initialValue === "courier")) &&
      hasItemIneligibleForCourier &&
      (isEligibleForHaulier || haulierFulfillmentType?.charge) &&
      haulierFulfillmentType &&
      palletOverrideEnabled;

    if (forceHaulier) {
      onUpdate("haulier");
    }
  }, [
    currentFulfillmentType,
    initialValue,
    hasItemIneligibleForCourier,
    haulierFulfillmentType,
    isEligibleForHaulier,
    onUpdate,
    palletOverrideEnabled,
    unbundlingEnabled,
  ]);

  useEffect(() => {
    if (
      unbundlingEnabled ||
      !basket ||
      hasItemIneligibleForCourier ||
      !palletOverrideEnabled
    ) {
      return;
    }

    {
      const orderHasInelgibleItem = basket.OrderItems?.some((item) => {
        return isIneligibleForFulfillmentType({
          fulfillmentType: courierFulfillmentType,
          item: item.Product,
        });
      });
      if (orderHasInelgibleItem) setHasItemIneligibleForCourier(true);
    }
  }, [
    basket,
    courierFulfillmentType,
    hasItemIneligibleForCourier,
    palletOverrideEnabled,
    setHasItemIneligibleForCourier,
    unbundlingEnabled,
  ]);

  return null;
};

const SellarImportsDeliveryCard = ({
  supplier,
  hasSdDeliveryArea,
  onClickMoreInfo,
  changeFulfillmentType,
  fulfillmentType,
}: {
  supplier: SupplierGetResponseType;
  hasSdDeliveryArea: boolean;
  onClickMoreInfo: () => void;
  changeFulfillmentType: (
    fulfillmentType: CheckoutSelectedFulfillmentType,
  ) => void;
  fulfillmentType: CheckoutSelectedFulfillmentType | null;
}) => {
  // change to sd fulfillment type if eligible and not currently selected
  useEffect(() => {
    if (hasSdDeliveryArea && fulfillmentType !== "sellarDistribution") {
      changeFulfillmentType("sellarDistribution");
    }
  }, [hasSdDeliveryArea, fulfillmentType, changeFulfillmentType]);

  return (
    <Card className={twMerge("bg-white", "flex flex-col gap-2 p-4")}>
      <Badge icon={"FERRY"}>Sellar imports</Badge>
      <p>
        Your order will be shipped from {supplier.Business.name} in{" "}
        {supplier.Business.DeliveryAddress.city},{" "}
        {
          CountryCodeDictionary[
            supplier.Business.DeliveryAddress
              .country as keyof typeof CountryCodeDictionary
          ]
        }{" "}
        to our warehouse.
      </p>
      <div
        onClick={onClickMoreInfo}
        className={
          "m-0 flex cursor-pointer flex-row items-center gap-2 p-0 text-blue-800"
        }
      >
        <Icon icon={"OPEN_IN_NEW"} />
        <p className={"font-semibold"}>MORE INFO</p>
      </div>
    </Card>
  );
};
